import {Box, Modal, Typography} from '@viptech/react-components'

import {sub} from 'date-fns'
import {capitalize} from 'lodash'
import moment from 'moment'
import {useSnackbar} from 'notistack'
import {useEffect, useState} from 'react'
import {BtnClose} from 'src/components/BtnClose'
import LoadMoreButton from 'src/components/Button/LoadMoreButton'
import Table from 'src/components/table/Table'
import TableData from 'src/components/table/components/tableData/TableData'
import {useAuth} from 'src/contexts/AuthContext'
import useClients from 'src/hooks/api/client/useClients'
import useStartStreamWithTimestamp from 'src/hooks/api/streaming/useStartStreamWithTimestamp'
import PlayerOnTable from 'src/pages/alarms/components/player-on-table/PlayerOnTable'
import VideoPlayer from 'src/pages/camera-config/components/camera-general-data/VideoPlayer'
import config from 'src/services/config'
import {KonvaImageDetectionsFullscreen} from '../../../../common/components/konva-image-detections-fullscreen/KonvaImageDetectionsFullscreen'
import {RowsAlarm} from '../../../../common/types/alarms/TableAlarm'
import RowHover from '../../../../components/RowHover/RowHover'
import {useCompany} from '../../../../contexts/CompanyContext'
import {Alarm, SnapshotEvent} from '../../../../services/api/endpoints/EventsEndpoint'
import {objectTypeList} from '../../AlarmsFunctions'
import ImageOnTable from '../image-on-table/ImageOnTable'
import {ContainerTable, NoDataBox} from '../styles'
import MenuOptions from './OptionsMenuComponent'

const columnsToUserB2C = [
  {
    id: 'id',
    name: 'ID',
  },
  {
    id: 'name',
    name: 'Nome da Câmera',
  },

  {
    id: 'objectTypeLabel',
    name: 'Tipo de Detecção',
  },
  {
    id: 'status',
    name: 'Resultado',
  },
  {
    id: 'reason',
    name: 'Motivos',
  },
  {
    id: 'createdAt',
    name: 'Data e hora',
  },
  {
    id: 'Options',
    name: 'Opções',
  },
]

const columnsToUserB2B = [
  {
    id: 'id',
    name: 'ID',
  },
  {
    id: 'name',
    name: 'Nome da Câmera',
  },
  {
    id: 'company',
    name: 'Empresa',
  },
  {
    id: 'objectTypeLabel',
    name: 'Tipo de Detecção',
  },
  {
    id: 'images',
    name: 'Imagens do Evento',
  },
  {
    id: 'status',
    name: 'Resultado',
  },
  {
    id: 'reason',
    name: 'Motivos',
  },
  {
    id: 'createdAt',
    name: 'Data e hora',
  },
  {
    id: 'Options',
    name: 'Opções',
  },
]

type Props = {
  isLoadingAlarms: boolean
  fetchNextPage: () => void
  isFetchingNextPage: boolean
  hasNextPage: boolean
  events: Alarm[]
}

function AlarmsGridTable(props: Props) {
  const {isLoadingAlarms, fetchNextPage, isFetchingNextPage, hasNextPage, events} = props
  const {userIsB2c} = useAuth()
  const {enqueueSnackbar} = useSnackbar()
  const {companies} = useCompany()

  const [rows, setRows] = useState<RowsAlarm[]>([])
  const [imageToRender, setImageToRender] = useState<SnapshotEvent>()
  const [eventToPlay, setEventToPlay] = useState<RowsAlarm>()
  const [startTimeStream, setStartTimeStream] = useState<number>(0)
  const [endTimeStream, setEndTimeStream] = useState<number | undefined>()

  useEffect(() => {
    const transformEventOnRows = events.map((event) => {
      const lastUpdate = new Date(event.lastUpdate)
      const createdAt = new Date(event.createdAt)
      const timeoutDate = new Date(Date.now() - config.eventInitTimeout)
      const isTimeout = moment(createdAt).isBefore(timeoutDate)
      const status = event.status.description ? capitalize(event.status.description?.toLowerCase()) : ''
      const listOfObjects = event.objects?.split(',')
      const translateObjects = objectTypeList.filter((object) => listOfObjects?.includes(object.name)).map((it) => it.label)
      const objectName = translateObjects.length > 1 ? translateObjects.concat(', ') : translateObjects
      const companyName = companies.find((it) => it.id === event.companyId)?.name ?? '-'
      const reasons = event.activatedActions?.length ? event.activatedActions.map((it) => it.reason).filter((it) => !!it) : ['-']
      const reasonsSet = new Set(reasons)

      return {
        id: event.id,
        name: event.camera.name ?? '-',
        company: companyName,
        objectTypeLabel: objectName,
        status: status === 'Iniciado' && isTimeout ? 'Timeout' : status,
        lastUpdate: lastUpdate.toLocaleString(),
        createdAt: createdAt.toLocaleString(),
        timeUpdate: moment(lastUpdate).fromNow().split(' ').slice(1).join(' '),
        self: event,
        imagesFromEvent: event.snapshots,
        reasons: Array.from(reasonsSet).join(', '),
      }
    })

    setRows(transformEventOnRows)
  }, [events])

  function handleLoadMore() {
    if (!hasNextPage) return enqueueSnackbar('Todos os eventos já foram carregados. Altere os filtros para pesquisar novamente', {variant: 'info'})
    fetchNextPage()
  }

  const {data: playerURL} = useStartStreamWithTimestamp({
    cameraId: eventToPlay?.self.camera.id ?? -1,
    enabled: eventToPlay !== undefined,
    start: startTimeStream,
    end: endTimeStream,
  })

  const {data: clients} = useClients({})
  const companyHasRecordingVideoPermission = true
  const [isErrorOnStream, setIsErrorOnStream] = useState(false)

  return (
    <Box height='100%' padding='0 20px'>
      <ContainerTable>
        <Table isLoading={isLoadingAlarms} skeletonRowsAmount={20} isOpenAdvancedSearch={false} columns={userIsB2c ? columnsToUserB2C : columnsToUserB2B}>
          {rows.map((row) => {
            const cameraOriginRT = row.self.camera.origin.includes('RTMP') || row.self.camera.origin.includes('RTSP')
            const getRetentionDays = clients?.data.data.entities.find((client) => client.id === row.self.clientId)?.daysToRetainStream ?? 1
            const lastRetentionDate = sub(new Date(), {days: getRetentionDays})
            const isEventOnPastFromRetentionDate = new Date(row.createdAt).getTime() < lastRetentionDate.getTime()
            const shouldRenderVideoPlayer = cameraOriginRT && companyHasRecordingVideoPermission && !isEventOnPastFromRetentionDate
            return (
              <RowHover id={`row-${row.id}`} cursorType='auto'>
                <TableData id={`row-id-${row.id}`}>{row.id}</TableData>
                <TableData id={`row-name-${row.id}`}>{row.name}</TableData>
                {!userIsB2c && (
                  <TableData id={`row-company-${row.id}`}>
                    <p style={{textOverflow: 'ellipsis', maxWidth: '240px', overflow: 'hidden', whiteSpace: 'nowrap'}}>{row.company}</p>
                  </TableData>
                )}
                <TableData id={`row-obj-${row.id}`}>{row.objectTypeLabel || '-'}</TableData>
                <TableData id={`row-images-${row.id}`}>
                  <Box display='flex' columnGap='5px' padding='5px'>
                    {shouldRenderVideoPlayer &&
                      row.imagesFromEvent
                        ?.filter((img) => img.demarcatedAreas.length)
                        .map((image, index) => {
                          if (index >= 1 && shouldRenderVideoPlayer) return <></>
                          return (
                            <>
                              <ImageOnTable
                                imageToRender={image}
                                key={image.id}
                                rowsLenght={rows.length}
                                imageSelected={setImageToRender}
                                showFeedback={row.status !== 'Correto' && row.status !== 'Incorreto'}
                              />
                              <PlayerOnTable
                                imageToRender={image}
                                key={row.id}
                                rowsLenght={rows.length}
                                imageSelected={() => {
                                  const SECONDS_BEFORE_EVENT = 5
                                  const SECONDS_AFTER_EVENT = 5
                                  const start = new Date(row.createdAt).getTime() / 1000 - SECONDS_BEFORE_EVENT
                                  const end = start + SECONDS_AFTER_EVENT
                                  setStartTimeStream(start)
                                  setEndTimeStream(end)
                                  setEventToPlay(row)
                                }}
                              />
                            </>
                          )
                        })}

                    {!shouldRenderVideoPlayer &&
                      row.imagesFromEvent?.map((image) => {
                        return (
                          <ImageOnTable
                            imageToRender={image}
                            key={image.id}
                            rowsLenght={rows.length}
                            imageSelected={setImageToRender}
                            showFeedback={row.status !== 'Correto' && row.status !== 'Incorreto'}
                          />
                        )
                      })}
                  </Box>
                </TableData>
                <TableData id={`row-sta-${row.id}`}>{row.status}</TableData>
                <TableData id={`row-reason-${row.id}`} width='200px'>
                  {row.reasons}
                </TableData>
                <TableData id={`row-created-${row.id}`}>{row.createdAt}</TableData>
                <TableData id={`row-opt-${row.id}`}>
                  <MenuOptions event={row.self} />
                </TableData>
              </RowHover>
            )
          })}
        </Table>
        {!isLoadingAlarms && rows.length === 0 && <NoDataBox>Nenhum registro encontrado</NoDataBox>}
      </ContainerTable>

      {!isLoadingAlarms && rows.length > 0 && (
        <Box style={{display: 'flex', justifyContent: 'center', alignItems: 'center', paddingTop: '6px'}}>
          <LoadMoreButton onClick={() => handleLoadMore()} isLoading={isFetchingNextPage} />
        </Box>
      )}

      {imageToRender && (
        <KonvaImageDetectionsFullscreen
          open={!!imageToRender}
          src={imageToRender.src}
          detections={imageToRender.demarcatedAreas.map((it) => ({
            x: it.startX,
            y: it.startY,
            width: it.endX,
            height: it.endY,
            data: it,
          }))}
          onClose={() => setImageToRender(undefined)}
        />
      )}

      {eventToPlay && (
        <Modal
          isOpen={!!eventToPlay}
          closeModal={() => setEventToPlay(undefined)}
          backgroundColor='white'
          boxShadow='rgba(202, 202, 202, 0.5) 1px 1px 1px'
          border=''>
          <Box display='flex' direction='column' rowGap='20px' padding='30px'>
            <BtnClose onClose={() => setEventToPlay(undefined)} />
            <Box>
              <Typography variant='p' size='14px' weight={600} children={eventToPlay.self.camera.name} />
              <Typography variant='p' size='12px'>
                <b>Evento: </b>
                {eventToPlay.self.id} - {eventToPlay.createdAt} - {eventToPlay.objectTypeLabel}
              </Typography>
            </Box>

            {playerURL && (
              <VideoPlayer
                src={playerURL}
                thumbnail=''
                drawBarrierCoordinates={false}
                loop={true}
                showControls={true}
                setIsErrorOnStream={setIsErrorOnStream}
              />
            )}
            {isErrorOnStream && (
              <Box padding='20px 0'>
                <Typography variant='p'>Não foi possível reproduzir a gravação do evento</Typography>
              </Box>
            )}
          </Box>
        </Modal>
      )}
    </Box>
  )
}

export default AlarmsGridTable
